import React, { useMemo } from "react";
import dayjs from "dayjs";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot, LineHighlightPlot } from "@mui/x-charts/LineChart";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";
import { ChartsClipPath } from "@mui/x-charts/ChartsClipPath";

import { DashboardEmptyState } from "../../components";
import { Stack } from "@mui/material";

import DashboardThroughputTooltip from "./DashboardThroughputTooltip";

const BAR_COLOR = "#df9e44";
const GOAL_LINE_COLOR = "#cd683e";
const GAP_LINE_COLOR = "#1F4E79";

const LABEL_FORMAT = {
  day: "MMM D",
  week: "MMM D",
  month: "MMM",
};

const formatCurrency = (val) => {
  if (val == null) return "$0";
  if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
};

const bucketEndDate = (key, groupBy) => {
  if (groupBy === "month") return dayjs(`${key}-01`).endOf("month");
  if (groupBy === "week") return dayjs(key).add(6, "day").endOf("day");
  return dayjs(key).endOf("day");
};

const buildDataset = (buckets, groupBy) => {
  const today = dayjs().startOf("day");
  let cum = 0;
  let passedToday = false;
  return buckets.map((b, idx) => {
    const revenue = Number(b.revenue) || 0;
    const goal = Number(b.goal) || 0;
    if (passedToday) {
      return { ...b, revenue, goal, gap: 0 };
    }
    if (!idx) {
      return { ...b, revenue, goal, gap: 0 };
    }
    cum += revenue - goal;
    const gap = cum;
    if (!bucketEndDate(b.key, groupBy).isBefore(today)) passedToday = true;
    return { ...b, revenue, goal, gap };
  });
};

const DashboardThroughputChart = ({
  buckets = [],
  groupBy = "day",
  isLoading,
}) => {
  const dataset = useMemo(
    () => buildDataset(buckets, groupBy),
    [buckets, groupBy],
  );
  const total = dataset.reduce((s, b) => s + b.revenue, 0);
  const fmt = LABEL_FORMAT[groupBy] || LABEL_FORMAT.day;
  const clipId = "dashboard-throughput-clip";

  const TooltipContent = (props) => (
    <DashboardThroughputTooltip {...props} groupBy={groupBy} />
  );

  if (!isLoading && total === 0) {
    return (
      <DashboardEmptyState
        icon="FileText"
        title="No completed work orders"
        subtitle="Once work orders fully complete their routing they'll appear here, bucketed by the latest route close date."
      />
    );
  }

  return (
    <Stack
      sx={{
        ".MuiLineElement-series-gap": {
          fill: "#80808050",
          stroke: "none",
        },
      }}
    >
      <ResponsiveChartContainer
        height={300}
        dataset={dataset}
        margin={{ top: 10, right: 20, bottom: 30, left: 60 }}
        xAxis={[
          {
            id: "bucket",
            scaleType: "band",
            dataKey: "key",
            valueFormatter: (k) => dayjs(k).format(fmt),
          },
        ]}
        yAxis={[{ id: "revenue", valueFormatter: formatCurrency }]}
        series={[
          {
            id: "revenue",
            type: "bar",
            dataKey: "revenue",
            label: "Throughput",
            color: BAR_COLOR,
            yAxisId: "revenue",
            valueFormatter: formatCurrency,
          },
          {
            id: "goal",
            type: "line",
            dataKey: "goal",
            label: "Goal",
            color: GOAL_LINE_COLOR,
            showMark: false,
            valueFormatter: formatCurrency,
          },
          {
            id: "gap",
            type: "line",
            dataKey: "gap",
            label: "Gap",
            color: GAP_LINE_COLOR,
            showMark: false,
            curve: "monotoneX",
            connectNulls: true,
            valueFormatter: formatCurrency,
          },
        ]}
      >
        <ChartsGrid horizontal />
        <g clipPath={`url(#${clipId})`}>
          <LinePlot slotProps={{ line: { strokeWidth: 2, fill: "none" } }} />
          <BarPlot
            slotProps={{
              bar: { clipPath: "inset(0px round 4px 4px 0px 0px)" },
            }}
          />
          <ChartsAxisHighlight x="band" />
        </g>
        <MarkPlot />
        <LineHighlightPlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="revenue" />
        <ChartsTooltip trigger="axis" axisContent={TooltipContent} />
        <ChartsClipPath id={clipId} />
      </ResponsiveChartContainer>
    </Stack>
  );
};

export default DashboardThroughputChart;
